/** The RFI register, out of the parked legacy page and into EDM.
 *
 * The column nobody had: BALL IN COURT. A register that shows a status and an assignee
 * cannot answer "who is this waiting on", because an answered RFI is waiting on the
 * person who asked, not the person who answered. It is derived server-side and the
 * summary across the top is a count per party, because that is the conversation.
 */
import { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, Plus, Send } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import { Btn, Drawer, Empty, Field, Input, Panel, Select, Stat } from '@/components/nfa/NfaPrimitives';

const STATUSES = ['', 'OPEN', 'RESPONDED', 'CLOSED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const EdmRfi = ({ projectId }) => {
  const T = useNfaTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [breachedOnly, setBreachedOnly] = useState(false);
  const [raise, setRaise] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', discipline: 'Piping',
    priority: 'MEDIUM', assigned_to: '', response_due: '' });
  const [dupes, setDupes] = useState([]);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (breachedOnly) p.set('breached_only', 'true');
    api.get(`/edm/projects/${projectId}/rfis?${p}`)
      .then((r) => setData(r.data)).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [projectId, status, breachedOnly]);
  useEffect(load, [load]);

  const openDetail = async (id) => {
    const r = await api.get(`/edm/projects/${projectId}/rfis/${id}`);
    setOpen(r.data); setReply(''); setNote('');
  };

  const checkDupes = async (subject) => {
    if (subject.trim().split(' ').length < 3) { setDupes([]); return; }
    try {
      const r = await api.post(`/edm/projects/${projectId}/rfis/check-duplicate`, { subject });
      setDupes(r.data.matches || []);
    } catch { setDupes([]); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/edm/projects/${projectId}/rfis`, form);
      toast.success('RFI raised');
      setRaise(false); setDupes([]);
      setForm({ subject: '', description: '', discipline: 'Piping', priority: 'MEDIUM',
        assigned_to: '', response_due: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not raise the RFI'); }
    setBusy(false);
  };

  const respond = async () => {
    setBusy(true);
    try {
      await api.post(`/edm/projects/${projectId}/rfis/${open.id}/respond`,
        { response: reply, status: 'RESPONDED' });
      toast.success('Response recorded');
      await openDetail(open.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not respond'); }
    setBusy(false);
  };

  const addNote = async () => {
    setBusy(true);
    try {
      await api.post(`/edm/projects/${projectId}/rfis/${open.id}/comments`, { body: note });
      setNote(''); await openDetail(open.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add the note'); }
    setBusy(false);
  };

  const tone = (c) => ({ red: T.red, amber: T.amber, emerald: T.green, sky: T.primary }[c]
    || T.muted);

  return (
    <div className="space-y-3" data-testid="edm-rfi">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat T={T} label="RFIs" value={data?.count ?? '—'} testId="edm-rfi-stat-total"
          info="Every RFI on this project, whatever its state." />
        <Stat T={T} label="Open" value={data?.open ?? '—'} testId="edm-rfi-stat-open"
          info="Raised and not yet answered." />
        <Stat T={T} label="Breached" value={data?.breached ?? '—'} tone="red"
          testId="edm-rfi-stat-breached"
          info="Past the response-due date with no response recorded." />
        <Stat T={T} label="Due soon" value={data?.dueSoon ?? '—'} tone="amber"
          testId="edm-rfi-stat-duesoon" info="Two days or fewer to the due date." />
        <Stat T={T} label="Waiting on" testId="edm-rfi-stat-bic"
          value={data?.ballInCourt?.[0]?.party || '—'}
          sub={data?.ballInCourt?.[0] ? `${data.ballInCourt[0].count} RFI(s)` : undefined}
          info={data?.note} />
      </div>

      <Panel T={T} testId="edm-rfi-panel" pad={false} title="Request for information"
        subtitle={data?.note}
        right={<>
          <Select T={T} value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="edm-rfi-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s || 'All states'}</option>)}
          </Select>
          <Btn T={T} variant={breachedOnly ? 'primary' : 'ghost'}
            testId="edm-rfi-breached-toggle"
            onClick={() => setBreachedOnly((v) => !v)}>Breached only</Btn>
          <Btn T={T} variant="ghost" testId="edm-rfi-export"
            onClick={() => window.open(
              `${process.env.REACT_APP_BACKEND_URL}/api/edm/projects/${projectId}/rfis/export.csv`,
              '_blank')}><Download className="w-3 h-3" /> CSV</Btn>
          <Btn T={T} testId="edm-rfi-raise" onClick={() => setRaise(true)}>
            <Plus className="w-3 h-3" /> Raise an RFI</Btn>
        </>}>
        {loading && <Empty T={T}><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</Empty>}
        {!loading && (data?.rfis || []).length === 0 && (
          <Empty T={T} testId="edm-rfi-empty">No RFI matches these filters.</Empty>
        )}
        {!loading && (data?.rfis || []).map((r) => (
          <button type="button" key={r.id} onClick={() => openDetail(r.id)}
            data-testid={`edm-rfi-row-${r.rfi_number}`}
            className="w-full text-left px-4 py-2.5 text-[11.5px] hover:opacity-90"
            style={{ borderBottom: `1px solid ${T.border}` }}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono" style={{ color: T.primary }}>{r.rfi_number}</span>
              <span style={{ color: T.ink }}>{r.subject}</span>
              <span className="text-[9.5px] px-1.5 py-0.5 rounded"
                style={{ background: T.tint(tone(r.sla.aging_color)),
                  color: tone(r.sla.aging_color) }}>{r.sla.aging_label}</span>
              <span className="text-[9.5px] px-1.5 py-0.5 rounded"
                data-testid={`edm-rfi-bic-${r.rfi_number}`}
                style={{ background: T.surfaceAlt, color: T.body }}>
                ball in court · {r.ballInCourt.waitingOn}
              </span>
              <span className="ml-auto text-[10px]" style={{ color: T.muted }}>
                {r.discipline} · {r.priority}
                {r.sla.open_days != null ? ` · ${r.sla.open_days}d open` : ''}
              </span>
            </div>
            <div className="mt-0.5 text-[10.5px]" style={{ color: T.muted }}>
              {r.ballInCourt.statement}
            </div>
          </button>
        ))}
      </Panel>

      <Drawer T={T} open={raise} onClose={() => setRaise(false)} title="Raise an RFI"
        subtitle="The subject is what appears in the log, in the minutes and in any claim that quotes it."
        testId="edm-rfi-drawer">
        <div className="space-y-3">
          <Field T={T} label="Subject">
            <Input T={T} value={form.subject} data-testid="edm-rfi-subject"
              onChange={(e) => { setForm({ ...form, subject: e.target.value });
                checkDupes(e.target.value); }} />
          </Field>
          {dupes.length > 0 && (
            <div className="rounded p-2 text-[10.5px]" data-testid="edm-rfi-dupes"
              style={{ background: T.tint(T.amber), color: T.amber }}>
              {dupes.length} existing RFI(s) are worded like this one. The answer to one of
              them may be the answer to this:
              <ul className="mt-1 space-y-0.5">
                {dupes.map((d) => (
                  <li key={d.rfi_id}>
                    <span className="font-mono">{d.rfi_number}</span> — {d.subject}
                    {d.response ? ` · answered: ${d.response}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Field T={T} label="Question">
            <textarea value={form.description} data-testid="edm-rfi-description"
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={5} className="w-full rounded-md px-2 py-1.5 text-[11.5px] outline-none"
              style={{ background: T.isDark ? T.surfaceAlt : '#FFF',
                border: `1px solid ${T.border}`, color: T.ink }} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field T={T} label="Discipline">
              <Input T={T} value={form.discipline} data-testid="edm-rfi-discipline"
                onChange={(e) => setForm({ ...form, discipline: e.target.value })} />
            </Field>
            <Field T={T} label="Priority">
              <Select T={T} value={form.priority} data-testid="edm-rfi-priority"
                onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
            <Field T={T} label="Assign to (email)"
              hint="An unassigned RFI ages without anybody being asked.">
              <Input T={T} value={form.assigned_to} data-testid="edm-rfi-assignee"
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} />
            </Field>
            <Field T={T} label="Response due">
              <Input T={T} type="date" value={form.response_due}
                data-testid="edm-rfi-due"
                onChange={(e) => setForm({ ...form, response_due: e.target.value })} />
            </Field>
          </div>
          <Btn T={T} onClick={submit} disabled={busy || !form.subject.trim()}
            testId="edm-rfi-submit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Raise it
          </Btn>
        </div>
      </Drawer>

      <Drawer T={T} open={!!open} onClose={() => setOpen(null)} width={820}
        title={open ? `${open.rfi_number} — ${open.subject}` : ''}
        subtitle={open?.ballInCourt?.statement} testId="edm-rfi-detail">
        {open && (
          <div className="space-y-3 text-[11.5px]" style={{ color: T.body }}>
            <div className="grid grid-cols-3 gap-2">
              <Stat T={T} label="State" value={open.status} testId="edm-rfi-detail-status" />
              <Stat T={T} label="Ageing" value={open.sla.aging_label}
                sub={open.sla.open_days != null ? `${open.sla.open_days} days open` : undefined}
                testId="edm-rfi-detail-ageing" />
              <Stat T={T} label="Ball in court" value={open.ballInCourt.waitingOn}
                sub={open.ballInCourt.role} testId="edm-rfi-detail-bic" />
            </div>
            <Panel T={T} title="The question" testId="edm-rfi-detail-question">
              <p style={{ whiteSpace: 'pre-wrap' }}>{open.description || '—'}</p>
            </Panel>
            {open.response ? (
              <Panel T={T} title="The response" testId="edm-rfi-detail-response"
                subtitle={`${open.response_by} · ${open.response_at}`}>
                <p style={{ whiteSpace: 'pre-wrap' }}>{open.response}</p>
              </Panel>
            ) : (
              <Panel T={T} title="Respond" testId="edm-rfi-detail-respond">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4}
                  data-testid="edm-rfi-reply"
                  className="w-full rounded-md px-2 py-1.5 text-[11.5px] outline-none"
                  style={{ background: T.isDark ? T.surfaceAlt : '#FFF',
                    border: `1px solid ${T.border}`, color: T.ink }} />
                <div className="mt-2">
                  <Btn T={T} onClick={respond} disabled={busy || !reply.trim()}
                    testId="edm-rfi-respond-submit">Record the response</Btn>
                </div>
              </Panel>
            )}
            <Panel T={T} title={`Thread (${(open.comments || []).length})`}
              testId="edm-rfi-thread">
              {(open.comments || []).map((c) => (
                <div key={c.id} className="py-1.5"
                  style={{ borderBottom: `1px solid ${T.border}` }}>
                  <div className="text-[10px]" style={{ color: T.muted }}>
                    {c.author} · {c.at} · {c.visibility}
                  </div>
                  <div>{c.body}</div>
                </div>
              ))}
              <div className="mt-2 flex gap-2">
                <Input T={T} value={note} data-testid="edm-rfi-note"
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="add a note, @mention an email to copy them…" />
                <Btn T={T} onClick={addNote} disabled={busy || !note.trim()}
                  testId="edm-rfi-note-submit">Add</Btn>
              </div>
            </Panel>
            <Panel T={T} title={`Audit trail (${(open.audit_trail || []).length})`}
              testId="edm-rfi-audit">
              {(open.audit_trail || []).map((a) => (
                <div key={a.id} className="text-[10.5px] py-0.5" style={{ color: T.muted }}>
                  {a.at} · {a.action} · {a.actor}
                </div>
              ))}
            </Panel>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default EdmRfi;
